import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { toast } from 'react-toastify'
import HttpHandler, { getModelChangeEvent } from 'app/reusable/HttpHandler'
import {
  FILTER_TYPE,
  activeFilters,
  buildListSearch,
  buildRequestParams,
  isSameFilters,
  parseListSearch,
} from './listQuery'

export const SEARCH_DEBOUNCE_MS = 300

/**
 * State + data for `ModelListPage`. The search text, page and filters live
 * in the URL (see `listQuery.js`); this hook reads them, fetches the
 * matching page from the backend's generic list API, and exposes setters
 * that write back to the URL. A filter or search change goes back to page 1.
 *
 * While a new page loads, the previous rows stay available (`isLoading` is
 * true, `rows` unchanged) so the list doesn't flash a spinner on every
 * keystroke; only the very first load has no rows (`isFirstLoad`).
 *
 * The list also reloads on the app's model-change event for its model
 * (`getModelChangeEvent`), fired by forms and actions elsewhere in the app.
 */
const useModelList = ({
  djangoApp,
  djangoModel,
  filters: definitions = [],
  defaultFilters = {},
  orderBy,
  pageSize = 20,
  baseParams,
}) => {
  const location = useLocation()
  const history = useHistory()

  const state = useMemo(
    () => parseListSearch(location.search, definitions, defaultFilters),
    // Definitions/defaults are static page config.
    [location.search] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const writeState = useCallback(
    (next) => {
      history.replace({
        pathname: location.pathname,
        search: buildListSearch(
          next,
          definitions,
          defaultFilters,
          location.search
        ),
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, location.pathname, location.search]
  )

  // Search input: local state so typing is instant, written to the URL
  // after a short pause.
  const [searchText, setSearchText] = useState(state.text)
  const lastWrittenText = useRef(state.text)

  useEffect(() => {
    // The URL changed from outside the input (back/forward, "clear").
    if (state.text !== lastWrittenText.current) {
      lastWrittenText.current = state.text
      setSearchText(state.text)
    }
  }, [state.text])

  useEffect(() => {
    if (searchText === state.text) return undefined
    const timeout = setTimeout(() => {
      lastWrittenText.current = searchText
      writeState({ ...state, text: searchText, page: 1 })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [searchText]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetching
  const [response, setResponse] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const requestId = useRef(0)

  const requestParams = useMemo(
    () =>
      buildRequestParams(state, definitions, { orderBy, pageSize, baseParams }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, orderBy, pageSize, JSON.stringify(baseParams)]
  )
  const requestKey = JSON.stringify(requestParams)

  const load = useCallback(() => {
    const id = ++requestId.current
    setIsLoading(true)

    return HttpHandler.list(djangoApp, djangoModel, requestParams)
      .then(({ data }) => {
        if (id === requestId.current) setResponse(data)
      })
      .catch((e) => {
        if (id !== requestId.current) return
        console.error('Erro ao buscar registros.', { e })
        toast.error('Erro ao buscar registros.')
      })
      .finally(() => {
        if (id === requestId.current) setIsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [djangoApp, djangoModel, requestKey])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const { type } = getModelChangeEvent(djangoApp, djangoModel)
    const onModelChange = () => load()
    window.addEventListener(type, onModelChange)
    return () => window.removeEventListener(type, onModelChange)
  }, [djangoApp, djangoModel, load])

  // Model filters only keep ids in the URL; their labels are remembered
  // when picked, or looked up once when the page opens from a link.
  const [modelLabels, setModelLabels] = useState({})
  const labelKey = (key, id) => `${key}:${id}`

  useEffect(() => {
    definitions
      .filter((definition) => definition.type === FILTER_TYPE.MODEL)
      .forEach((definition) => {
        const missing = (state.filters[definition.key]?.values || []).filter(
          (id) => !modelLabels[labelKey(definition.key, id)]
        )
        if (!missing.length) return

        HttpHandler.list(definition.djangoApp, definition.djangoModel, {
          select_return: true,
          paginate_by: missing.length,
          id: missing,
        })
          .then(({ data }) => {
            const found = Object.fromEntries(
              (data?.data || []).map(({ value, label }) => [
                labelKey(definition.key, value),
                label,
              ])
            )
            setModelLabels((prev) => ({ ...prev, ...found }))
          })
          .catch(console.error)
      })
  }, [state.filters]) // eslint-disable-line react-hooks/exhaustive-deps

  const filters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(state.filters).map(([key, value]) => {
          const definition = definitions.find((d) => d.key === key)
          if (definition?.type !== FILTER_TYPE.MODEL) return [key, value]
          const labels = Object.fromEntries(
            value.values.map((id) => [id, modelLabels[labelKey(key, id)]])
          )
          return [key, { ...value, labels }]
        })
      ),
    [state.filters, modelLabels] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const setFilter = useCallback(
    (key, value) => {
      if (value?.labels) {
        setModelLabels((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(value.labels)
              .filter(([, label]) => label)
              .map(([id, label]) => [labelKey(key, id), label])
          ),
        }))
      }
      writeState({
        ...state,
        text: searchText,
        page: 1,
        filters: { ...state.filters, [key]: value },
      })
    },
    [state, searchText, writeState]
  )

  const clearFilters = useCallback(
    () => writeState({ ...state, text: searchText, page: 1, filters: {} }),
    [state, searchText, writeState]
  )

  const restoreDefaults = useCallback(
    () =>
      writeState({
        ...state,
        text: searchText,
        page: 1,
        filters: activeFilters(definitions, defaultFilters),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, searchText, writeState]
  )

  const setPage = useCallback(
    (page) => writeState({ ...state, page }),
    [state, writeState]
  )

  const paginator = response?.paginator

  return {
    rows: response?.data ?? [],
    title: response?.title,
    count: paginator?.count,
    page: paginator?.current_page_number ?? state.page,
    pageCount: paginator?.last_page_number ?? 1,
    requestParams,
    // Changes whenever the filters or search do (not the page): selections
    // and other per-result state reset on it.
    resultsKey: JSON.stringify({ ...requestParams, page: undefined }),
    isLoading,
    isFirstLoad: isLoading && response === null,
    filters,
    hasActiveFilters:
      Object.keys(activeFilters(definitions, state.filters)).length > 0,
    hasDefaultFilters:
      Object.keys(activeFilters(definitions, defaultFilters)).length > 0,
    isDefaultFilters: isSameFilters(definitions, state.filters, defaultFilters),
    searchText,
    setSearchText,
    setFilter,
    clearFilters,
    restoreDefaults,
    setPage,
    reload: load,
  }
}

export default useModelList
